import { apiClient, setAuthToken, clearAuthToken, setBusinessDetails, clearAuth, BusinessDetails } from './api/client';

export interface RevenueManagerConfig {
  /**
   * Base URL for the API
   * @default 'https://api.dev.mettastay.com/api/v4'
   */
  apiBaseUrl?: string;
  
  /**
   * Authentication token for API requests
   */
  authToken?: string;
  
  /**
   * Business details for API requests
   */
  businessDetails?: BusinessDetails | string;
  
  /**
   * Enable mock mode for development/testing
   * @default false
   */
  useMock?: boolean;
}

let currentConfig: RevenueManagerConfig = {};

/**
 * Configure the Revenue Manager SDK
 * Call this before using any components or hooks
 */
export function configureRevenueManager(config: RevenueManagerConfig): void {
  currentConfig = { ...currentConfig, ...config };
  
  // Update axios base URL
  if (config.apiBaseUrl) {
    apiClient.defaults.baseURL = config.apiBaseUrl;
  }
  
  // Set auth token
  if (config.authToken) {
    setAuthToken(config.authToken);
  }
  
  // Set business details
  if (config.businessDetails) {
    setBusinessDetails(config.businessDetails);
  }
  
  // Handle ngrok URLs
  if (config.apiBaseUrl?.includes('ngrok')) {
    apiClient.defaults.headers['ngrok-skip-browser-warning'] = 'true';
  }
}

/**
 * Get the current configuration
 */
export function getConfig(): RevenueManagerConfig {
  return { ...currentConfig };
}

/**
 * Update the authentication token
 */
export function updateAuthToken(token: string): void {
  currentConfig.authToken = token;
  setAuthToken(token);
}

/**
 * Update the business details
 */
export function updateBusinessDetails(details: BusinessDetails | string): void {
  currentConfig.businessDetails = details;
  setBusinessDetails(details);
}

/**
 * Clear the authentication token
 */
export function removeAuthToken(): void {
  currentConfig.authToken = undefined;
  clearAuthToken();
}

/**
 * Clear all authentication data (token and business details)
 */
export function removeAuth(): void {
  currentConfig.authToken = undefined;
  currentConfig.businessDetails = undefined;
  clearAuth();
}

/**
 * Check if mock mode is enabled
 */
export function isMockMode(): boolean {
  return currentConfig.useMock ?? false;
}
